import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import * as z from 'zod/v4';

import { CanonicalDatasetKind } from '@watershed/constants/datasets';

import { socialAndGovernanceTypeNames } from '../batSchemas/types';

export const DEFAULT_EM_DESCRIPTION = `EM run generated from product`;
export const DEFAULT_HIDDEN_FOOTPRINT_DESCRIPTION =
  'Hidden publish auto-generated by creation workflow';
export const DEFAULT_HIDDEN_FOOTPRINT_NAME =
  'Hidden internal publish from auto-generated footprint';
export const HISTORICAL_EMISSIONS_BAT = 'historical_emissions';
export const HISTORICAL_EMISSIONS_PV2_BAT = 'historical_emissions_pv2';

// Where did we get the number 50,000? Assuming the dedicated task queue takes
// 30-90s to spin up, we think 50,000 is a reasonable lower limit because (as of
// 2024-09-04) its p50 run time is almost 8 minutes, so users are unlikely to be
// disturbed by an extra 30-90s.
export const DEDICATED_TASK_QUEUE_MIN_INPUT_ROW_COUNT = 50_000;

// We're seeing that (as of 2024-10-06) `createFootprintSnapshowWorkflow` runs
// has a p50 run time of 6 minutes for 1M rows, so users are unlikely to be
// disturbed by an extra 30-90s.
export const DEDICATED_TASK_QUEUE_MIN_PLR_ROW_COUNT = 1_000_000;

// Threshold for determining "small" footprints that can use shorter activity
// timeouts. Footprints with fewer than this many pipeline results (and fewer
// than DEDICATED_TASK_QUEUE_MIN_PLR_ROW_COUNT rows) are considered small.
export const SMALL_FOOTPRINT_MAX_PLR_COUNT = 500;

// Max BARTS per EM run. This is to prevent us from hitting
// temporal workflow history limits and message size limits, though
// we have not done any testing to see what the actual limits are.
export const MAX_BARTS_PER_EM_RUN = 100;

// Max historical_emissions BARTs per chunked createPrecalculatedEmissionsWorkflow
// run.
export const MAX_HISTORICAL_EMISSIONS_BARTS_PER_CHUNK = 10;

/**
 * This documents what counts as DuckDbTshirtSize.Large when looking at
 * emissions result variables count vs EME row count. All other cases are
 * automatically `DuckDbTshirtSize.Large`.
 */
export const EME_VARIABLE_COUNT_TO_ROW_THRESHOLD = [
  { countLimit: 8, rowCountThreshold: 5_000_000 },
  { countLimit: 16, rowCountThreshold: 2_500_000 },
  { countLimit: 25, rowCountThreshold: 2_000_000 },
  { countLimit: 35, rowCountThreshold: 1_500_000 },
  { countLimit: 60, rowCountThreshold: 800_000 },
];

// This is the threshold for when we consider a PLR to be "large" and start
// sending relevant DuckDb requests to XL and bumping up timeouts
export const LARGE_PLR_ROW_COUNT_THRESHOLD = 3_500_000;

// This is the threshold for when we consider there to be enough tags passed
// to footprint assembly to be "large" and sending the DuckDb requests to XL
export const LARGE_TAG_COUNT_THRESHOLD = 100;

export const FOOTPRINT_SNAPSHOT_DRILLDOWN_PARAM = 'footprintSnapshotId';
export const FOOTPRINT_SNAPSHOT_IDS_DRILLDOWN_PARAM = 'footprintSnapshotIds';
export const DRILLDOWN_DRAWER_PARAM = 'drawer';

// to deprecate
export const ACTIVITY_DRAWER_QUERY_PARAM = 'viewActivity';

// These are NOT emissive, NOT used for intensity calculation, and we don't want
// them to be associated with a footprint.
export const DATASETS_TO_IGNORE_FOR_FOOTPRINT: Array<CanonicalDatasetKind> = [
  CanonicalDatasetKind.ChartOfAccounts,
];

export const INTENSITY_DENOMINATOR_BATS = [
  'intensity_denominator_custom',
  'intensity_denominator_monetary',
];

// A list of BATs that will be associated with a footprint but are non-emissive.
// (if adding to this list: don't forget to update NON_EMISSIVE_DATASET_KINDS in CanonicalDatasetUtils.ts too)
export const NON_EMISSIVE_ACTIVITY_BATS = [
  'site_land_use',
  'sold_product_circularity',
  'sold_product_recyclability',
  'carbon_credit_retirements',
  'ghg_removal',
  ...socialAndGovernanceTypeNames,
];

export const NON_EMISSIVE_BATS = [
  ...INTENSITY_DENOMINATOR_BATS,
  ...NON_EMISSIVE_ACTIVITY_BATS,
];

export const ALL_HISTORICAL_EMISSIONS_BATS = [
  HISTORICAL_EMISSIONS_BAT,
  HISTORICAL_EMISSIONS_PV2_BAT,
];

// order matters
export const FOOTPRINT_CONFIGURATION_DRAWER_TABS_SCHEMA = z.enum([
  'general',
  'datasets',
  'calculation',
  'category-rules',
  'deduplication-rules',
  'clean-power',
]);
export const FOOTPRINT_CONFIGURATION_DRAWER_TABS =
  FOOTPRINT_CONFIGURATION_DRAWER_TABS_SCHEMA.enum;
export type FootprintConfigurationDrawerTab = z.infer<
  typeof FOOTPRINT_CONFIGURATION_DRAWER_TABS_SCHEMA
>;

export const SAVED_VIEW_DRAFT_ID = 'draft'; // Used as the value for an overflow/other category group name throughout drilldown + reductions
// Should not be displayed as is

export const LEGACY_FOOTPRINT_NAME = 'Legacy Footprint';
export const LEGACY_FOOTPRINTS_NO_EDIT_WARNING: MessageDescriptor = msg`Legacy footprints can't be edited`;
export const FOOTPRINT_NAME_MUST_TRIM_ERROR_MSG: MessageDescriptor = msg`Footprint name cannot start or end with whitespace`;
export const FOOTPRINT_NAME_NOT_UNIQUE_ERROR_MSG: MessageDescriptor = msg`Footprint name is already used`;
export const FOOTPRINT_NAME_NOT_EMPTY_ERROR_MSG: MessageDescriptor = msg`Footprint name cannot be empty`;

export const MAX_CONCURRENT_FOOTPRINT_GENERATION_TOOLTIP_MSG: MessageDescriptor = msg`Unable to create or edit footprints while another footprint is being generated. Please try again in a few minutes.`;
export const MAX_CONCURRENT_FOOTPRINT_GENERATION = 1;
export const MAX_FOOTPRINT_GENERATION_RUNTIME_HOURS = 6;
export const MAX_FOOTPRINT_GENERATION_RUNTIME_MILLISECONDS =
  MAX_FOOTPRINT_GENERATION_RUNTIME_HOURS * 60 * 60 * 1000;
export const MILLISECONDS_TO_WAIT_FOR_WORKFLOW_TO_START = 3 * 60 * 1000; // 3 minutes

export const MAX_FOOTPRINT_AUDIT_BUNDLE_RUNTIME_MILLISECONDS = 30 * 60 * 1000; // 30 minutes

export enum SetupFootprintGenerationError {
  ADTWithoutMeasurementProjectId = 'ADTWithoutMeasurementProjectId',
  OnlyIntensityDatasets = 'OnlyIntensityDatasets',
  MissingExtBarts = 'MissingExtBarts',
}

export type AdeInformationForHistoricalResults = {
  id: string;
  extBartInstanceId: string;
  bartId: string;
  bartActivityType: string;
};

export type BartInformationForHistoricalResults = {
  extBartInstanceId: string;
  activityType: string;
};

export enum ActivityDataFootprintUsageKind {
  Emissive = 'Emissive',
  IntensityDenominator = 'IntensityDenominator',
  NonEmissive = 'NonEmissive',
}

// Signals used by the syncFootprintSnapshotsToBigQueryWorkflow
export const MAYBE_START_SYNCING_SIGNAL = 'maybeStartSyncing';
export const DEFINITELY_START_SYNCING_SIGNAL = 'definitelyStartSyncing';
